import axiosInstance from '../axiosConfig';
import {
  Program,
  ProgramFormData,
  ProgramEnrollment,
  ProgramEnrollmentFormData,
  ProgramSession,
  ProgramsResponse,
  ProgramResponse,
  ProgramEnrollmentResponse,
  ProgramEnrollmentsResponse,
  ProgramSessionsResponse,
} from '../../types';

/**
 * Program Service
 * Handles all Program-related API calls
 */
class ProgramService {
  private baseUrl = '/programs';

  // ==========================================
  // Program Listing Endpoints
  // ==========================================

  /**
   * Get all published programs available for enrollment
   * @param programType - Optional filter by program type
   * @returns List of published programs
   */
  async getPublishedPrograms(programType?: string): Promise<Program[]> {
    const params: any = {};
    if (programType) params.type = programType;

    const response = await axiosInstance.get<ProgramsResponse>(`${this.baseUrl}/`, { params });
    return response.data.data;
  }

  /**
   * Get all programs including drafts and inactive (Staff only)
   * @param includeInactive - Include inactive programs
   * @returns List of all programs
   */
  async getAllPrograms(includeInactive: boolean = false): Promise<Program[]> {
    const response = await axiosInstance.get<ProgramsResponse>(
      `${this.baseUrl}/all`,
      { params: { include_inactive: includeInactive ? 'true' : 'false' } }
    );
    return response.data.data;
  }

  /**
   * Get program by ID with full details
   * @param programId - Program ID
   * @returns Program details with sessions and enrollment count
   */
  async getProgramById(programId: string): Promise<Program> {
    const response = await axiosInstance.get<ProgramResponse>(`${this.baseUrl}/${programId}`);
    return response.data.data;
  }

  // ==========================================
  // Program Management Endpoints (Staff)
  // ==========================================

  /**
   * Create a new program (Staff only)
   * @param data - Program form data
   * @returns Created program
   */
  async createProgram(data: ProgramFormData): Promise<Program> {
    const response = await axiosInstance.post<ProgramResponse>(`${this.baseUrl}/`, data);
    return response.data.data;
  }

  /**
   * Update a program (Staff only)
   * @param programId - Program ID
   * @param data - Updated program data
   * @returns Updated program
   */
  async updateProgram(programId: string, data: Partial<ProgramFormData>): Promise<Program> {
    const response = await axiosInstance.put<ProgramResponse>(
      `${this.baseUrl}/${programId}`,
      data
    );
    return response.data.data;
  }

  /**
   * Publish a draft program (Staff only)
   * @param programId - Program ID
   * @returns Published program
   */
  async publishProgram(programId: string): Promise<Program> {
    const response = await axiosInstance.post<ProgramResponse>(
      `${this.baseUrl}/${programId}/publish`
    );
    return response.data.data;
  }

  /**
   * Cancel a program (Staff only)
   * @param programId - Program ID
   * @returns Cancelled program
   */
  async cancelProgram(programId: string): Promise<Program> {
    const response = await axiosInstance.delete<ProgramResponse>(`${this.baseUrl}/${programId}`);
    return response.data.data;
  }

  // ==========================================
  // Enrollment Endpoints
  // ==========================================

  /**
   * Enroll in a program
   * @param programId - Program ID
   * @param data - Optional enrollment data (member_id or non_member_id for staff enrolling someone else)
   * @returns Created enrollment
   */
  async enrollInProgram(programId: string, data?: ProgramEnrollmentFormData): Promise<ProgramEnrollment> {
    const response = await axiosInstance.post<ProgramEnrollmentResponse>(
      `${this.baseUrl}/${programId}/enroll`,
      data || {}
    );
    return response.data.data;
  }

  /**
   * Get current user's program enrollments
   * @param includePast - Include past enrollments
   * @returns List of enrollments
   */
  async getMyEnrollments(includePast: boolean = false): Promise<ProgramEnrollment[]> {
    const response = await axiosInstance.get<ProgramEnrollmentsResponse>(
      `${this.baseUrl}/enrollments/my`,
      { params: { include_past: includePast ? 'true' : 'false' } }
    );
    return response.data.data;
  }

  /**
   * Get all enrollments for a program (Staff only)
   * @param programId - Program ID
   * @param status - Optional status filter
   * @returns List of enrollments
   */
  async getProgramEnrollments(programId: string, status?: string): Promise<ProgramEnrollment[]> {
    const params: any = {};
    if (status) params.status = status;

    const response = await axiosInstance.get<ProgramEnrollmentsResponse>(
      `${this.baseUrl}/${programId}/enrollments`,
      { params }
    );
    return response.data.data;
  }

  /**
   * Cancel an enrollment
   * @param enrollmentId - Enrollment ID
   * @returns Cancelled enrollment
   */
  async cancelEnrollment(enrollmentId: string): Promise<ProgramEnrollment> {
    const response = await axiosInstance.delete<ProgramEnrollmentResponse>(
      `${this.baseUrl}/enrollments/${enrollmentId}`
    );
    return response.data.data;
  }

  // ==========================================
  // Helper Methods
  // ==========================================

  /**
   * Format price for display
   * @param price - Price in decimal
   * @returns Formatted price string
   */
  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  }

  /**
   * Format date for display
   * @param dateString - Date string (YYYY-MM-DD or ISO)
   * @returns Formatted date string
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * Format date range for display
   * @param startDate - Start date string
   * @param endDate - End date string
   * @returns Formatted date range
   */
  formatDateRange(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const startFormatted = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const endFormatted = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return `${startFormatted} - ${endFormatted}`;
  }

  /**
   * Format recurrence pattern for display
   * @param pattern - Recurrence pattern object
   * @returns Human-readable schedule string
   */
  formatRecurrencePattern(pattern: { days: number[]; time: string; duration: number }): string {
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const days = pattern.days.map(d => dayNames[d]).join(', ');

    // Parse time
    const [hour, min] = pattern.time.split(':').map(Number);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    const timeStr = `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;

    return `${days} at ${timeStr} (${pattern.duration} min)`;
  }

  /**
   * Get day names from day indices
   * @param days - Array of day indices (0-6, Monday=0)
   * @returns Array of day names
   */
  getDayNames(days: number[]): string[] {
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days.map(d => dayNames[d]);
  }

  /**
   * Get program type label
   * @param type - Program type
   * @returns Human-readable type label
   */
  getProgramTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      tennis: 'Tennis',
      pickleball: 'Pickleball',
      fitness: 'Fitness',
      swimming: 'Swimming',
      golf: 'Golf',
      other: 'Other',
    };
    return labels[type] || type;
  }

  /**
   * Get enrollment status label
   * @param status - Enrollment status
   * @returns Human-readable status label with color hint
   */
  getEnrollmentStatusLabel(status: string): { label: string; color: string } {
    const statuses: Record<string, { label: string; color: string }> = {
      registered: { label: 'Registered', color: 'success' },
      waitlisted: { label: 'Waitlisted', color: 'warning' },
      cancelled: { label: 'Cancelled', color: 'error' },
      completed: { label: 'Completed', color: 'default' },
    };
    return statuses[status] || { label: status, color: 'default' };
  }

  /**
   * Get program status label
   * @param status - Program status
   * @returns Human-readable status label with color hint
   */
  getProgramStatusLabel(status: string): { label: string; color: string } {
    const statuses: Record<string, { label: string; color: string }> = {
      draft: { label: 'Draft', color: 'default' },
      published: { label: 'Published', color: 'success' },
      cancelled: { label: 'Cancelled', color: 'error' },
      completed: { label: 'Completed', color: 'info' },
    };
    return statuses[status] || { label: status, color: 'default' };
  }
}

// Export singleton instance
const programService = new ProgramService();
export default programService;
